import type { DealOrigin, DealStage, DealType } from './dealModel';

/**
 * The Deal's own rules (piece 18): the contact rows, the team and its
 * shares, the agreement and expansion pair, the loss, the quick route, and
 * the one automation rule an administrator writes.
 */
export const QUICK_DEAL_THRESHOLD_KEY = 'deals.quickDealApprovalThreshold';
export const OWNER_ESCALATION_WINDOW_KEY = 'deals.ownerEscalationWindowHours';
export const PRICE_STALENESS_DAYS_KEY = 'pricing.priceStalenessDays';
export const COST_PROJECTION_RATE_KEY = 'pricing.costProjectionRatePercent';

/** The threshold ships empty by design; the window defaults to 48 hours; the two pricing numbers to 90 days and 15 percent. */
export const PIECE_18_SETTINGS: readonly { key: string; value: number }[] = [
  { key: OWNER_ESCALATION_WINDOW_KEY, value: 48 },
  { key: PRICE_STALENESS_DAYS_KEY, value: 90 },
  { key: COST_PROJECTION_RATE_KEY, value: 15 },
];

/** 🔒 Decision 12700: Entered in Error ends a Deal made by mistake that holds a document, which is never deleted. */
export const ENTERED_IN_ERROR_REASON = 'Entered in Error';
export const LOSS_REASON_SEED = ['Price', 'Timing', 'Competitor', 'No Budget', 'No Decision', 'Lost Contact', 'Went In-House', ENTERED_IN_ERROR_REASON] as const;
export const DEAL_TEAM_ROLE_SEED = ['Engineer', 'Field Services', 'Sales'] as const;
export const OWNER_TEAM_ROLE = 'Sales';

export interface ContactLike {
  id: string;
  employmentId: string;
  companyId: string;
  isPrimary: boolean;
  endDate?: Date | string | null;
}

/** 🔨 Changing the Account ends every contact row whose Employment is not at the new company; a row is never deleted (decision 0076). */
export function contactsToEnd(rows: readonly ContactLike[], newCompanyId: string): ContactLike[] {
  return rows.filter((row) => !row.endDate && row.companyId !== newCompanyId);
}

/** Exactly one Primary per Deal: checking a new one un-checks the old rather than raising an error. */
export function primaryAfter(rows: readonly ContactLike[], newPrimaryId: string): { id: string; isPrimary: boolean }[] {
  return rows.filter((row) => !row.endDate).map((row) => ({ id: row.id, isPrimary: row.id === newPrimaryId }));
}

export interface TeamShareLike {
  isPrimary: boolean;
  commissionSharePercent: number | null;
  endDate?: Date | string | null;
}

/** 🔨 The owner holds the remainder; explicit shares sum to at most 100, so turning a share on can never overpay. Null means on the team and not in the payout. */
export function sharesProblem(rows: readonly TeamShareLike[], candidate?: { commissionSharePercent: number | null }): string | null {
  const open = rows.filter((r) => !r.endDate && !r.isPrimary);
  const total = open.reduce((sum, r) => sum + (r.commissionSharePercent ?? 0), 0) + (candidate?.commissionSharePercent ?? 0);
  if (candidate?.commissionSharePercent !== undefined && candidate.commissionSharePercent !== null && (candidate.commissionSharePercent < 0 || candidate.commissionSharePercent > 100)) return 'a share is between 0 and 100 percent';
  return total > 100 + 1e-9 ? `the team's explicit shares would sum to ${total}%, above the 100% the owner's remainder allows` : null;
}

export function ownerRemainder(rows: readonly TeamShareLike[]): number {
  return Math.max(0, 100 - rows.filter((r) => !r.endDate && !r.isPrimary).reduce((sum, r) => sum + (r.commissionSharePercent ?? 0), 0));
}

/** 🔨 At most one of agreementId and expandedFromDealId is set; coverage is the stronger statement (decisions 0044, 0154). */
export function expansionProblem(agreementId: string | null | undefined, expandedFromDealId: string | null | undefined): string | null {
  return agreementId && expandedFromDealId ? 'a Deal sells under an Agreement or expands an earlier Deal, never both; coverage is the stronger statement' : null;
}

/** 🔨 Lost needs a reason inside the one dialog; a Deal at Lost with no reason is not a reachable state. */
export function lossProblem(input: { lossReasonId?: string | null; stage: DealStage }): string | null {
  if (input.stage !== 'Lost') return null;
  return input.lossReasonId ? null : 'marking a Deal Lost needs its reason, chosen in the same dialog';
}

/**
 * 🔒 Marking a Deal Lost closes its open Quotes in the same save (piece 18; T2350). Open is stored Sent or
 * Client Viewed, an Expired one included, since Expired is derived from a Sent row; a Draft is no live offer,
 * and an Accepted, Declined or Void Quote is already closed, so each is left as it is.
 */
export const LOST_DEAL_OPEN_QUOTE_STATUSES = ['Sent', 'ClientViewed'] as const;

/** The two ends the Lost dialog offers each open Quote: Declined, the customer's answer, or Void, a withdrawal on our side. */
export const LOST_QUOTE_OUTCOMES = ['declined', 'void'] as const;
export type LostQuoteOutcome = (typeof LOST_QUOTE_OUTCOMES)[number];

/** One open Quote's end as the Lost save's body names it. */
export interface LostQuoteChoice {
  quoteId: string;
  outcome: LostQuoteOutcome;
}

/** One open Quote as the Lost dialog lists it: the printed number, the total in cents and the displayed status. */
export interface LostDealQuote {
  id: string;
  number: string;
  totalCents: number;
  status: string;
}

/** Whether a stored Quote status is one the Lost save closes. */
export function closesWithLostDeal(status: string): boolean {
  return (LOST_DEAL_OPEN_QUOTE_STATUSES as readonly string[]).includes(status);
}

/** What is wrong with the Lost save's list of Quote ends, or null: each names a Quote once, with Declined or Void. */
export function lostQuotesProblem(quotes: unknown): string | null {
  if (quotes === undefined || quotes === null) return null;
  if (!Array.isArray(quotes)) return 'The Quotes to close are a list.';
  const seen = new Set<string>();
  for (const choice of quotes as unknown[]) {
    const row = (choice ?? {}) as { quoteId?: unknown; outcome?: unknown };
    if (typeof row.quoteId !== 'string' || row.quoteId === '') return 'Each Quote to close is named by its id.';
    if (!(LOST_QUOTE_OUTCOMES as readonly unknown[]).includes(row.outcome)) return 'Each Quote closes as Declined or Void.';
    if (seen.has(row.quoteId)) return 'A Quote is named once in the list.';
    seen.add(row.quoteId);
  }
  return null;
}

/** The Quick Deal above the configured threshold requires approval rather than being blocked; an unset threshold holds nothing. */
export function quickDealNeedsApproval(quoteTotal: number, threshold: number | null | undefined): boolean {
  return threshold !== null && threshold !== undefined && threshold > 0 && quoteTotal > threshold;
}

/**
 * The hold, evaluated on every write of the value on a QuickBooked Deal —
 * creation, the BOM's floor raise and the Quote total (decision 0353). An
 * approved Deal is never held again; Emergency is never held at all. The
 * answer is the columns the write sets, or null where it sets none.
 */
export function quickHoldPatch(deal: { origin: DealOrigin; quickApprovedAt?: Date | string | null }, value: number, threshold: number | null | undefined, now: Date): { quickApprovalHeldAt: Date } | null {
  if (deal.origin !== 'QuickBooked' || deal.quickApprovedAt) return null;
  return quickDealNeedsApproval(value, threshold) ? { quickApprovalHeldAt: now } : null;
}

export type QuickApprovalDecision = 'Approve' | 'Reject';
export const QUICK_APPROVAL_DECISIONS: readonly QuickApprovalDecision[] = ['Approve', 'Reject'];

/** 🔨 dealType sets a default route and never decides the Deal is already won; the value on a quick route is the Quote total, never typed. */
export function valueInputReadOnly(origin: DealOrigin): boolean {
  // 🔒 A request's value is its billing minimum, then its issued invoices (decision 1912), and is never typed either.
  return origin === 'QuickBooked' || origin === 'Emergency' || origin === 'ServiceRequest';
}

/** A Deal is created at Won on the Quick, the Emergency and the service request routes, at Intent on the pipeline. */
export function openingStage(origin: DealOrigin): DealStage {
  return origin === 'Pipelined' ? 'Intent' : 'Won';
}

// ---- Stage actions (decision 0213) ----

export const STAGE_ACTION_KINDS = ['CreateToDo', 'Notify'] as const;
export type StageActionKind = (typeof STAGE_ACTION_KINDS)[number];

export const TITLE_TOKENS = ['deal', 'customer', 'value'] as const;

/** The title takes `{deal}`, `{customer}` and `{value}` and nothing else; any other token is refused at save. */
export function titleTemplateProblem(template: string): string | null {
  if (!template.trim()) return 'a rule carries a title; type the title people will read on the task this rule creates';
  const bad = [...template.matchAll(/\{([^}]*)\}/g)].map((m) => m[1]!).filter((token) => !(TITLE_TOKENS as readonly string[]).includes(token));
  return bad.length > 0 ? `the title takes {deal}, {customer} and {value} only; ${bad.map((b) => `{${b}}`).join(', ')} is not a token` : null;
}

export function renderTitle(template: string, deal: { name: string; customer: string; value: string }): string {
  return template.replace(/\{deal\}/g, () => deal.name).replace(/\{customer\}/g, () => deal.customer).replace(/\{value\}/g, () => deal.value);
}

export interface StageActionLike {
  id: string;
  stage: DealStage;
  subStage: string | null;
  dealType: DealType | null;
  action: StageActionKind;
  isActive: boolean;
}

/** The rules that fire on reaching a stage, or on ticking a sub-stage, for a deal type or any; a not-applicable sub-stage fires nothing. */
export function rulesFiring(rules: readonly StageActionLike[], event: { stage: DealStage; subStage: string | null; dealType: DealType; applicable: boolean }, alreadyFired: ReadonlySet<string>): StageActionLike[] {
  if (!event.applicable) return [];
  return rules.filter((rule) => rule.isActive && rule.stage === event.stage && (rule.subStage ?? null) === (event.subStage ?? null) && (rule.dealType === null || rule.dealType === event.dealType) && !alreadyFired.has(rule.id));
}

/** A to-do's due date is a business-day offset, the reminder engine's own rule; there is one such walk, not two. */
export { addBusinessDays } from '../reminders/schedule';

// ---- The absent owner (decisions 0079, 0144) ----

export interface ReportingLine {
  employmentId: string;
  bossEmploymentId: string | null;
}

export interface ResponsibleAnswer {
  /** The manager the action escalates to, or the root when the chain ends there. */
  employmentId: string | null;
  chain: string[];
  /** A break in the chain: an employment with no boss before the root, logged rather than guessed. */
  broken: boolean;
}

/**
 * `responsibleFor(employmentId, onDate)`: walks `EmploymentPosition.bossEmploymentId`
 * as at the date, stops at the root employment `Organization.rootEmploymentId`
 * names, and reports a break. HR extends the same walk with approved PTO; there
 * is one walk, not two. 🔨 `Deal.ownerPersonId` is untouched by the answer.
 */
export function responsibleFor(employmentId: string, bossOf: (employmentId: string) => string | null | undefined, rootEmploymentId: string | null): ResponsibleAnswer {
  const chain: string[] = [];
  const seen = new Set<string>([employmentId]);
  let current = employmentId;
  while (current !== rootEmploymentId) {
    const boss = bossOf(current) ?? null;
    if (!boss) return { employmentId: chain[chain.length - 1] ?? null, chain, broken: true };
    if (seen.has(boss)) return { employmentId: chain[chain.length - 1] ?? null, chain, broken: true };
    seen.add(boss);
    chain.push(boss);
    current = boss;
    if (chain.length > 64) return { employmentId: null, chain, broken: true };
  }
  return { employmentId: chain[0] ?? null, chain, broken: false };
}

/**
 * The one relationship exception no FieldVisibilityRule audience can express
 * (decision 0125): the row's own person and their manager read the commission
 * share a general rule withholds. The manager is whoever the row's employment
 * reports to as at today, resolved by the caller from EmploymentPosition.
 */
export function isSelfOrManager(viewer: { personId: string; employmentId: string }, row: { personId: string; managerEmploymentIds: readonly string[] }): boolean {
  return viewer.personId === row.personId || row.managerEmploymentIds.includes(viewer.employmentId);
}
